import { $http } from "../config/axiosConfig";
import { endpoints } from "../constants/api";
import { routePaths } from "../constants/paths";
import { loginSchema, signupSchema } from "../schemas/auth.schema";
import { LoginProps, SignupProps } from "../types";
import { decodeToken } from "../utils/jwt";
import Cookies from "js-cookie";
import { useNavigate } from "react-router";
import { useSessionStore } from "../store/session.store";

export const setToken = (name: string, value: string) => {
  Cookies.set(name, value, {
    secure: true, // Ensures the cookie is only sent over HTTPS
    sameSite: "Strict",
  });
};

export const useAuth = () => {
  const setUser = useSessionStore((state) => state.setUser);
  const setSessionExpiryDate = useSessionStore(
    (state) => state.setSessionExpiryDate
  );
  const navigate = useNavigate();

  const loginUser = async ({ values, callbackUrl }: LoginProps) => {
    const isValidFields = await loginSchema.isValid(values);

    if (isValidFields) {
      try {
        let payload = {
          email: values?.email,
          password: values?.password,
        };

        const data = await $http.post(endpoints.login, payload);

        if (data?.data?.token) {
          let decodedToken = decodeToken(data?.data?.token);

          // Get expiration time in milliseconds less 10 mins
          const expirationTimeMs = decodedToken?.exp
            ? (decodedToken?.exp - 600) * 1000
            : null;
          const expiryDate = expirationTimeMs
            ? new Date(expirationTimeMs).toISOString()
            : null;

          setSessionExpiryDate(expiryDate);
          setUser(data?.data?.user);
          setToken("token", data?.data?.token);

          navigate(callbackUrl || routePaths.dashboard);
        }
      } catch (error) {
        return error;
      }
    } else {
      return new Response("Invalid fields!", { status: 401 });
    }
  };

  const signupUser = async ({ values }: SignupProps) => {
    const isValidFields = await signupSchema.isValid({
      ...values,
      confirmPassword: values.password,
    });

    if (isValidFields) {
      try {
        const payload = {
          name: values.name,
          email: values.email,
          contacts: values.phone,
          password: values.password,
        };

        const data = await $http.post(endpoints.signup, payload);

        if (data?.data?.token) {
          let decodedToken = decodeToken(data?.data?.token);

          const expirationTimeMs = decodedToken?.exp
            ? (decodedToken?.exp - 600) * 1000
            : null;
          const expiryDate = expirationTimeMs
            ? new Date(expirationTimeMs).toISOString()
            : null;

          setSessionExpiryDate(expiryDate);
          setUser(data?.data?.user);
          setToken("token", data?.data?.token);

          navigate(routePaths.dashboard);
        }

        return { success: true };
      } catch (error) {
        return error;
      }
    } else {
      return new Response("Invalid fields!", { status: 401 });
    }
  };

  const logoutUser = async (callbackUrl?: string) => {
    setUser(null);
    setSessionExpiryDate(null);
    setToken("token", "");

    navigate(callbackUrl || routePaths.login);
  };

  return {
    loginUser,
    signupUser,
    logoutUser,
  };
};
